// City-level HOST recruitment pages (/become-a-host/<city>-ma). Import-free except
// cityLocations (which the build-time prerenderer loads with a lucide stub). Each
// page reuses the city's unique localIntro / transportation / use-case data so the
// pages are locally distinct, not doorway duplicates.
import { CITIES, CITY_ORDER, BELO_PROVIDER } from "@/data/cityLocations";

const SITE = "https://belorentals.com";

export const cityHostSlug = (citySlug) => `become-a-host/${citySlug}-ma`;
export const CITY_HOST_SLUGS = (CITY_ORDER || Object.keys(CITIES)).filter((c) => CITIES[c]);

const TRADEMARK = "Turo is a trademark of Turo Inc. Belo Rentals is not affiliated with, endorsed by, or sponsored by Turo.";
const INSURANCE = "Hosts maintain their own insurance and are responsible for claims. Belo Rentals does not provide host insurance or physical-damage protection.";

export function cityHostConfig(citySlug) {
  const c = CITIES[citySlug];
  if (!c) return null;
  const name = c.cityName;
  const path = `/${cityHostSlug(citySlug)}`;
  const nearby = (c.nearbyCities || []).filter((n) => CITIES[n]);
  return {
    citySlug,
    cityName: name,
    path,
    badge: `${name}, Massachusetts hosts`,
    h1: `Rent Out Your Car in ${name}, MA`,
    metaTitle: `Rent Out Your Car in ${name}, MA | Belo Rentals Hosts`,
    metaDescription: `List your car in ${name} on Belo Rentals — set your own price, keep your full listed rate with no host commission, and get weekly Friday Stripe payouts. A local Massachusetts marketplace.`,
    keywords: `rent out your car ${name}, list your car ${name} MA, car sharing host ${name}, make money renting your car ${name} Massachusetts`,
    intro: `Have a car in ${name} that sits parked between trips? List it on Belo Rentals, set your daily, weekly, and monthly prices, and keep the full listed rate you choose — Belo does not take a host commission. You stay in control of your calendar, your pricing, and where your vehicle can travel.`,
    highlights: [
      { icon: "Percent", title: "No host commission", body: `When your ${name} listing rents at your price, you receive the full listed rate. Belo never deducts a host commission.` },
      { icon: "DollarSign", title: "You set the price", body: "Choose your daily, weekly, and monthly rates, delivery fee, and mileage allowance." },
      { icon: "CalendarCheck", title: "Friday payouts", body: "Eligible earnings are initiated by Stripe ACH direct deposit every Friday." },
    ],
    localIntro: c.localIntro,
    demand: c.transportationContext || [],
    useCases: c.localUseCases || [],
    nearby,
    faqs: [
      { q: `How much can I earn renting out my car in ${name}?`, a: `It depends on your price, availability, and local demand — there is no guaranteed amount, and we don't market hosting as passive income. Use the earnings calculator on our Become a Host page to estimate. Belo takes no host commission from your listed rate.` },
      { q: `Do I need special insurance to host in ${name}?`, a: `Yes. You maintain your own valid insurance and must confirm with your insurer that your policy permits rental/marketplace use. Belo Rentals does not provide host insurance or claim coverage.` },
      { q: `What kind of car can I list in ${name}?`, a: `There is currently no maximum vehicle age or mileage. Financed and leased vehicles may apply if your agreement permits rental use. Salvage and rebuilt titles are not accepted. Your car must be registered, insured, inspected when required, safe, and clean.` },
      { q: `Can ${name} hosts also list on Turo?`, a: `Yes — you can list on Belo and Turo. You're responsible for keeping calendars synced to avoid double bookings.` },
    ],
    insurance: INSURANCE,
    trademark: TRADEMARK,
  };
}

export function cityHostJsonLd(cfg) {
  const pathAbs = `${SITE}${cfg.path}`;
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: cfg.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const crumbLd = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Become a Host", item: `${SITE}/become-a-host` },
    { "@type": "ListItem", position: 3, name: cfg.h1, item: pathAbs },
  ] };
  const serviceLd = { "@context": "https://schema.org", "@type": "Service", serviceType: "Car rental host marketplace", name: cfg.h1, areaServed: { "@type": "City", name: `${cfg.cityName}, MA` }, provider: { ...BELO_PROVIDER } };
  return [faqLd, crumbLd, serviceLd];
}
